import { Button } from '@repo/ui/components/ui/button'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { WorkspaceRouteShell } from '@/components/app/route-shells'
import { AppTopBarActionGroup, AppTopBarActions } from '@/components/app/top-bar-actions'
import { BusinessProfileEditor } from '@/components/operator/business-profile-editor'
import { OperatorWorkspaceRouteGate } from '@/components/operator/operator-workspace-route-gate'
import { useOperatorConfiguration } from '@/lib/operator-config/use-operator-configuration'
import type { RouteStaticData } from '@/lib/router/breadcrumbs'

export const Route = createFileRoute('/_app/business')({
  component: BusinessPage,
  staticData: {
    breadcrumbs: [{ label: 'Profile', href: '/business' }],
  } satisfies RouteStaticData,
})

function BusinessPage() {
  const { configuration, isLoading, error, refreshAll } = useOperatorConfiguration()
  const submitBusinessProfileRef = useRef<(() => Promise<boolean>) | null>(null)
  const [submitReady, setSubmitReady] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const handleRegisterSubmit = useCallback((submit: () => Promise<boolean>) => {
    submitBusinessProfileRef.current = submit
    setSubmitReady((current) => current || true)
  }, [])

  const handleSave = useCallback(async () => {
    if (!submitBusinessProfileRef.current || isSaving) return

    setIsSaving(true)
    try {
      await submitBusinessProfileRef.current()
    } finally {
      setIsSaving(false)
    }
  }, [isSaving])

  return (
    <WorkspaceRouteShell>
      <AppTopBarActions>
        <AppTopBarActionGroup>
          <Button
            type="button"
            size="sm"
            disabled={!submitReady || isSaving}
            onClick={() => void handleSave()}
          >
            {isSaving ? <Loader2 className="size-4 animate-spin" /> : null}
            Save
          </Button>
        </AppTopBarActionGroup>
      </AppTopBarActions>
      <OperatorWorkspaceRouteGate
        configuration={configuration}
        isLoading={isLoading}
        error={error}
        onRetry={refreshAll}
        loadingCopy="Loading profile…"
        resourceLabel="profile"
        noOperatorTitle="Create your profile."
        noOperatorDescription="Add your business name and booking link before configuring customer-facing workflows."
      >
        <BusinessProfileEditor
          configuration={configuration}
          onSaved={refreshAll}
          hideSubmitButton
          onRegisterSubmit={handleRegisterSubmit}
        />
      </OperatorWorkspaceRouteGate>
    </WorkspaceRouteShell>
  )
}
